/** EDP-00003 — Enterprise AI Experience framework (mirrors pkg/aix) */

export const EDP_AIX_ID = 'EDP-00003';

export type Philosophy =
  | 'ai_first'
  | 'human_always_controls'
  | 'explain_before_execute'
  | 'context_before_prompt'
  | 'business_before_technology'
  | 'transparent_reasoning'
  | 'learning_over_time'
  | 'progressive_assistance'
  | 'trust_through_evidence'
  | 'minimal_interruptions';

export type AIRole =
  | 'business_advisor'
  | 'catalog_specialist'
  | 'listing_expert'
  | 'advertising_manager'
  | 'seo_expert'
  | 'inventory_planner'
  | 'operations_manager'
  | 'financial_analyst'
  | 'brand_manager'
  | 'creative_director'
  | 'data_analyst'
  | 'executive_assistant'
  | 'coach'
  | 'teacher'
  | 'reviewer'
  | 'quality_auditor';

export type InteractionMode =
  | 'global_assistant'
  | 'contextual'
  | 'inline'
  | 'selection'
  | 'workspace'
  | 'bulk'
  | 'planning'
  | 'simulation'
  | 'review'
  | 'approval'
  | 'learning'
  | 'background';

export type EntryPoint =
  | 'command_palette'
  | 'keyboard_shortcut'
  | 'floating_action_button'
  | 'sidebar_copilot'
  | 'inline_action'
  | 'context_menu'
  | 'toolbar'
  | 'recommendation_card'
  | 'notification_center'
  | 'dashboard_widget';

export type RecCategory =
  | 'critical'
  | 'high_impact'
  | 'optimization'
  | 'opportunity'
  | 'maintenance'
  | 'learning'
  | 'experimental'
  | 'future_planning';

export type SimulationType =
  | 'price_change'
  | 'budget_change'
  | 'inventory_change'
  | 'promotion_run'
  | 'image_change'
  | 'keyword_improve'
  | 'review_improve'
  | 'competition_change';

export type ApprovalAction =
  | 'approve'
  | 'reject'
  | 'modify'
  | 'compare'
  | 'simulate_again'
  | 'escalate'
  | 'delegate'
  | 'schedule'
  | 'batch_approval';

export type ContextField =
  | 'workspace'
  | 'organization'
  | 'marketplace'
  | 'brand'
  | 'product'
  | 'asin'
  | 'campaign'
  | 'time_period'
  | 'user_role'
  | 'permissions'
  | 'previous_actions'
  | 'analytics'
  | 'knowledge_graph'
  | 'brand_rules'
  | 'product_dna'
  | 'prompt_rules'
  | 'recent_recommendations';

export interface ExplanationModel {
  what: string;
  why: string;
  evidence?: string[];
  expected_impact?: string;
  confidence: number;
  alternatives?: string[];
  risks?: string[];
  dependencies?: string[];
  estimated_effort?: string;
  next_step?: string;
}

export interface ReasoningPresentation {
  summary: string;
  evidence?: string[];
  key_factors?: string[];
  tradeoffs?: string[];
  confidence_level: string;
  supporting_data?: string[];
}

export interface AIXRecommendation {
  id: string;
  tenant_id: string;
  category: RecCategory;
  role?: AIRole;
  mode?: InteractionMode;
  title: string;
  explanation: ExplanationModel;
  reasoning: ReasoningPresentation;
  requires_approval: boolean;
  automation_level: number;
  source_module?: string;
  created_at: string;
}

export interface SimulationRecord {
  id: string;
  tenant_id: string;
  type: SimulationType;
  title: string;
  description?: string;
  projected_impact?: string;
  confidence: number;
  status: string;
  generated_at: string;
}

export interface RoleSpec {
  role: AIRole;
  label: string;
  description?: string;
  skills?: string[];
  modules?: string[];
}

export interface AIXDashboard {
  tenant_id: string;
  active_roles: number;
  interaction_modes: number;
  entry_points: number;
  open_recommendations: number;
  pending_approvals: number;
  active_simulations: number;
  context_fields: number;
  generated_at: string;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'ai_first', label: 'AI First', description: 'AI insight in every major workspace; background intelligence active' },
  { id: 'human_always_controls', label: 'Human Always Controls', description: 'No silent execution at L3+; approval gates enforced' },
  { id: 'explain_before_execute', label: 'Explain Before Execute', description: 'What, Why, Evidence, Impact before consequential actions' },
  { id: 'context_before_prompt', label: 'Context Before Prompt', description: 'Context engine assembles scope — user never repeats context' },
  { id: 'business_before_technology', label: 'Business Before Technology', description: 'Recommendations framed in revenue, margin, and brand outcomes' },
  { id: 'transparent_reasoning', label: 'Transparent Reasoning', description: 'Human-friendly reasoning; never raw chain-of-thought' },
  { id: 'learning_over_time', label: 'Learning Over Time', description: 'Feedback from accept/reject/outcomes improves recommendations' },
  { id: 'progressive_assistance', label: 'Progressive Assistance', description: 'Summary first; detail on demand; avoid overload' },
  { id: 'trust_through_evidence', label: 'Trust Through Evidence', description: 'Every claim linked to source module or metric' },
  { id: 'minimal_interruptions', label: 'Minimal Interruptions', description: 'Batch non-critical items; respect focus mode' },
];

export const INTERACTION_MODES: { id: InteractionMode; label: string }[] = [
  { id: 'global_assistant', label: 'Global Assistant' },
  { id: 'contextual', label: 'Contextual' },
  { id: 'inline', label: 'Inline' },
  { id: 'selection', label: 'Selection' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'bulk', label: 'Bulk' },
  { id: 'planning', label: 'Planning' },
  { id: 'simulation', label: 'Simulation' },
  { id: 'review', label: 'Review' },
  { id: 'approval', label: 'Approval' },
  { id: 'learning', label: 'Learning' },
  { id: 'background', label: 'Background' },
];

export const ENTRY_POINTS: { id: EntryPoint; label: string; shortcut?: string }[] = [
  { id: 'command_palette', label: 'Command Palette', shortcut: '⌘K' },
  { id: 'keyboard_shortcut', label: 'Copilot Toggle', shortcut: '⌘J' },
  { id: 'floating_action_button', label: 'Floating Action Button' },
  { id: 'sidebar_copilot', label: 'Sidebar Copilot' },
  { id: 'inline_action', label: 'Inline Actions' },
  { id: 'context_menu', label: 'Context Menu' },
  { id: 'toolbar', label: 'Toolbar Actions' },
  { id: 'recommendation_card', label: 'Recommendation Cards' },
  { id: 'notification_center', label: 'Notification Center' },
  { id: 'dashboard_widget', label: 'Dashboard Widgets' },
];

export const REC_CATEGORIES: { id: RecCategory; label: string; priority: string }[] = [
  { id: 'critical', label: 'Critical', priority: 'P0' },
  { id: 'high_impact', label: 'High Impact', priority: 'P1' },
  { id: 'optimization', label: 'Optimization', priority: 'P2' },
  { id: 'opportunity', label: 'Opportunity', priority: 'P2' },
  { id: 'maintenance', label: 'Maintenance', priority: 'P3' },
  { id: 'learning', label: 'Learning', priority: 'P4' },
  { id: 'experimental', label: 'Experimental', priority: 'P4' },
  { id: 'future_planning', label: 'Future Planning', priority: 'P5' },
];

export const APPROVAL_ACTIONS: { id: ApprovalAction; label: string }[] = [
  { id: 'approve', label: 'Approve' },
  { id: 'reject', label: 'Reject' },
  { id: 'modify', label: 'Modify' },
  { id: 'compare', label: 'Compare' },
  { id: 'simulate_again', label: 'Simulate Again' },
  { id: 'escalate', label: 'Escalate' },
  { id: 'delegate', label: 'Delegate' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'batch_approval', label: 'Batch Approval' },
];

export const CONTEXT_FIELDS: { id: ContextField; label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'organization', label: 'Organization' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'brand', label: 'Brand' },
  { id: 'product', label: 'Product' },
  { id: 'asin', label: 'ASIN' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'time_period', label: 'Time Period' },
  { id: 'user_role', label: 'User Role' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'previous_actions', label: 'Previous Actions' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'knowledge_graph', label: 'Knowledge Graph' },
  { id: 'brand_rules', label: 'Brand Rules' },
  { id: 'product_dna', label: 'Product DNA' },
  { id: 'prompt_rules', label: 'Prompt Rules' },
  { id: 'recent_recommendations', label: 'Recent Recommendations' },
];

export const AI_ROLES: RoleSpec[] = [
  { role: 'business_advisor', label: 'Business Advisor', description: 'Strategic growth and portfolio guidance', skills: ['gap_analysis', 'roadmap'], modules: ['biops'] },
  { role: 'catalog_specialist', label: 'Catalog Specialist', description: 'Listing quality and catalog health', skills: ['listing_audit'], modules: ['catalogops'] },
  { role: 'listing_expert', label: 'Listing Expert', description: 'SEO and conversion optimization', skills: ['keyword_optimization'], modules: ['contentintel'] },
  { role: 'advertising_manager', label: 'Advertising Manager', description: 'Campaign optimization and ROAS', skills: ['bid_optimization'], modules: ['revenueops'] },
  { role: 'seo_expert', label: 'SEO Expert', description: 'Organic ranking performance', skills: ['search_term_analysis'], modules: ['searchintel'] },
  { role: 'inventory_planner', label: 'Inventory Planner', description: 'Stock and replenishment', skills: ['forecast', 'reorder'], modules: ['inventoryintel'] },
  { role: 'operations_manager', label: 'Operations Manager', description: 'Orders and fulfillment', skills: ['order_health'], modules: ['orderops'] },
  { role: 'financial_analyst', label: 'Financial Analyst', description: 'Settlements and fees', skills: ['fee_analysis'], modules: ['financeops'] },
  { role: 'brand_manager', label: 'Brand Manager', description: 'Brand registry and IP', skills: ['brand_health'], modules: ['brandops'] },
  { role: 'creative_director', label: 'Creative Director', description: 'Images and brand store', skills: ['creative_brief'], modules: ['aicreative'] },
  { role: 'data_analyst', label: 'Data Analyst', description: 'Metrics and forecasting', skills: ['kpi_analysis'], modules: ['analytics'] },
  { role: 'executive_assistant', label: 'Executive Assistant', description: 'Daily ops briefing', skills: ['briefing'], modules: ['commandcenter'] },
  { role: 'coach', label: 'Coach', description: 'Guided learning', skills: ['onboarding'] },
  { role: 'teacher', label: 'Teacher', description: 'Platform concepts', skills: ['explain'] },
  { role: 'reviewer', label: 'Reviewer', description: 'Pre-execution review', skills: ['checklist'] },
  { role: 'quality_auditor', label: 'Quality Auditor', description: 'Audit and governance', skills: ['audit'], modules: ['governance'] },
];

export const DEMO_RECOMMENDATIONS: AIXRecommendation[] = [
  {
    id: 'aix-rec-1',
    tenant_id: 'demo',
    category: 'high_impact',
    role: 'advertising_manager',
    mode: 'contextual',
    title: 'Increase SP Brand Defense budget $150 → $180',
    explanation: {
      what: 'Raise daily budget on top-performing SP campaign',
      why: 'ROAS 6.73 with 94.9% budget utilization; lost impression share 18%',
      evidence: ['revenueops: campaign SP-EARBUDS-BRAND', 'amazonadsintel: impression share report'],
      expected_impact: '+$4,200/month sales at current ROAS',
      confidence: 0.91,
      alternatives: ['Increase bid instead of budget', 'Expand keywords first'],
      risks: ['ACOS may rise if conversion drops'],
      estimated_effort: '5 minutes',
      next_step: 'Simulate budget change',
    },
    reasoning: {
      summary: 'Campaign is budget-constrained with strong ROAS; incremental budget has high marginal return',
      key_factors: ['ROAS 6.73', '18% lost impression share', 'NTB 27.8%'],
      confidence_level: 'high',
      supporting_data: ['30-day spend: $4,230', 'Sales: $28,450'],
    },
    requires_approval: true,
    automation_level: 3,
    source_module: 'revenueops',
    created_at: new Date().toISOString(),
  },
  {
    id: 'aix-rec-2',
    tenant_id: 'demo',
    category: 'critical',
    role: 'financial_analyst',
    mode: 'workspace',
    title: 'File FBA reimbursement — 3 lost units EARBUDS-RED-003',
    explanation: {
      what: 'Submit SAFE-T reimbursement claim for 3 lost FBA units',
      why: '45 days since inventory loss with no reimbursement received',
      evidence: ['inventoryintel: 3 units lost FC-IND-1', 'financeops: no matching credit'],
      expected_impact: 'Recover $89.97',
      confidence: 0.94,
      next_step: 'Open post-sales ops reimbursement workflow',
    },
    reasoning: { summary: 'Inventory intel detected lost units exceeding reimbursement SLA threshold', confidence_level: 'high' },
    requires_approval: true,
    automation_level: 4,
    source_module: 'financeops',
    created_at: new Date().toISOString(),
  },
];

export const DEMO_SIMULATIONS: SimulationRecord[] = [
  {
    id: 'sim-1',
    tenant_id: 'demo',
    type: 'budget_change',
    title: 'Budget +20% SP Brand Defense',
    description: 'Simulate increasing daily budget $150 → $180',
    projected_impact: '+$4,200 sales, +$570 spend, ROAS 6.5 maintained',
    confidence: 0.88,
    status: 'completed',
    generated_at: new Date().toISOString(),
  },
  {
    id: 'sim-2',
    tenant_id: 'demo',
    type: 'price_change',
    title: 'CASE-PRO price $24.99 → $22.99',
    description: 'Simulate 8% price reduction impact on margin and volume',
    projected_impact: '+12% units, -2pp margin, net +$340/month',
    confidence: 0.76,
    status: 'draft',
    generated_at: new Date().toISOString(),
  },
];

export function buildAIXDashboard(): AIXDashboard {
  const pending = DEMO_RECOMMENDATIONS.filter((r) => r.requires_approval).length;
  const activeSims = DEMO_SIMULATIONS.filter((s) => s.status === 'draft' || s.status === 'running').length;
  return {
    tenant_id: 'demo',
    active_roles: AI_ROLES.length,
    interaction_modes: INTERACTION_MODES.length,
    entry_points: ENTRY_POINTS.length,
    open_recommendations: DEMO_RECOMMENDATIONS.length,
    pending_approvals: pending,
    active_simulations: activeSims,
    context_fields: CONTEXT_FIELDS.length,
    generated_at: new Date().toISOString(),
  };
}

export function categoryLabel(cat: RecCategory): string {
  return REC_CATEGORIES.find((c) => c.id === cat)?.label ?? cat.replace(/_/g, ' ');
}

export function roleLabel(role: AIRole): string {
  return AI_ROLES.find((r) => r.role === role)?.label ?? role.replace(/_/g, ' ');
}
